import { ComponentProps, useMemo, useState } from "react";
import styles from "./PrintOptionsModal.module.scss";
import ConfirmModal from "components/confirm-modal";
import ToggleSwitch from "components/toggle-switch";
import messages from "messages";
import useToggleModal from "hooks/useToggleModal";

export default function PrintOptionsModal({
  show,
  onCancel,
  onConfirm,
  options,
  setOptions,
  action = "print",
}: Pick<
  ComponentProps<typeof ConfirmModal>,
  "show" | "onConfirm" | "onCancel"
> & {
  options: TemplateContentOptions;
  setOptions: React.Dispatch<React.SetStateAction<TemplateContentOptions>>;
  action: "download" | "print";
}) {
  const labelTitle = action === "print" ? "Print Options" : "PDF Options";
  return (
    <ConfirmModal
      contentClassName={styles.printOptionsModal}
      labelCancel={messages.cancel}
      show={show}
      onCancel={onCancel}
      onConfirm={onConfirm}
      labelTitle={labelTitle}
      children={<PrintOptions options={options} setOptions={setOptions} />}
      labelConfirm={messages.confirm}
    />
  );
}

function PrintOptions({
  options,
  setOptions,
}: {
  options: TemplateContentOptions;
  setOptions: React.Dispatch<React.SetStateAction<TemplateContentOptions>>;
}) {
  return (
    <div className={styles.optionsModalContent}>
      <div className={styles.title}>
        Show all unselected options for PDF/Print view for:
      </div>
      <div className={styles.option}>
        <ToggleSwitch
          id="unselected-options-for-radios"
          type="checkbox"
          checked={!options.hideUnselectedOptionsForRadios}
          onChange={(e) =>
            setOptions((prev) => ({
              ...prev,
              hideUnselectedOptionsForRadios: !e.target.checked,
            }))
          }
        />
        <label htmlFor="unselected-options-for-radios">
          Multiple Choice / Multiple Choice Grid
        </label>
      </div>
      <div className={styles.option}>
        <ToggleSwitch
          id="unselected-options-for-checkboxes"
          type="checkbox"
          checked={!options.hideUnselectedOptionsForCheckboxes}
          onChange={(e) =>
            setOptions((prev) => ({
              ...prev,
              hideUnselectedOptionsForCheckboxes: !e.target.checked,
            }))
          }
        />
        <label htmlFor="unselected-options-for-checkboxes">
          Checkboxes / Checkboxes Grid
        </label>
      </div>
    </div>
  );
}

export function usePrintOptionsModal({
  onConfirm,
  action = "print",
}: {
  onConfirm: () => void;
  action?: "download" | "print";
}) {
  const {
    show: isShowingPrintOptionsModal,
    open: openPrintOptionsModal,
    close: closePrintOptionsModal,
  } = useToggleModal(false);

  const [printOptions, setPrintOptions] = useState<TemplateContentOptions>({
    hideUnselectedOptionsForRadios: true,
    hideUnselectedOptionsForCheckboxes: true,
  });

  const printOptionsModal = useMemo(
    () => (
      <PrintOptionsModal
        show={isShowingPrintOptionsModal}
        onCancel={closePrintOptionsModal}
        onConfirm={() => {
          closePrintOptionsModal();
          onConfirm();
        }}
        action={action}
        options={printOptions}
        setOptions={setPrintOptions}
      />
    ),
    [
      isShowingPrintOptionsModal,
      printOptions,
      closePrintOptionsModal,
      onConfirm,
      action,
    ],
  );

  return {
    openPrintOptionsModal,
    printOptions,
    printOptionsModal,
  };
}
